import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useT } from "@/i18n/I18nContext";
import { formatDate, formatDateTime } from "@/lib/dates";
import { getTraceability } from "@/lib/api";
import { toast } from "sonner";
import {
  ScanSearch, Search, Printer, Milk, FlaskConical, Truck, AlertTriangle,
  Package, CalendarClock, FileText, Recycle,
} from "lucide-react";

const fmtDate = (d) => formatDate(d);
const money = (v, c = "RON") =>
  v == null ? "—" : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;
const num = (v, suffix = "") => (v == null || v === "" ? "—" : `${Number(v).toLocaleString()}${suffix}`);

const matchKey = {
  production: "trace.matchProduction",
  lot: "trace.matchLot",
  order: "trace.matchOrder",
  delivery_note: "trace.matchDelivery",
  subproduct: "trace.matchSubproduct",
  raw_material: "rawMaterials.title",
};

const Field = ({ label, value, mono }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className={`text-sm font-medium text-foreground ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
    <Icon className="h-4 w-4 text-[hsl(var(--brand))]" />
    {children}
  </div>
);

const ProductionFile = ({ file, t, onTrace }) => {
  const p = file.production;
  const cur = file.sales_totals?.currency || "RON";
  const totalsByUnit = Object.entries(file.sales_totals?.by_unit || {});
  // Translate backend QC issue strings ("below min (X)", "above max (X)", "non-conforming option").
  const issueText = (s) => {
    if (!s) return "";
    if (s === "non-conforming option") return t("ncr.issueFailOption");
    return String(s).replace("below min", t("ncr.belowMin")).replace("above max", t("ncr.aboveMax"));
  };
  const TraceLink = ({ code, testId }) =>
    code ? (
      <button
        type="button"
        onClick={() => onTrace?.(code)}
        className="font-mono text-xs font-medium text-[hsl(var(--brand))] underline-offset-2 hover:underline focus-visible:underline"
        title={t("trace.traceThis")}
        data-testid={testId}
      >
        {code}
      </button>
    ) : (
      <span className="font-mono text-xs">—</span>
    );
  return (
    <Card className="overflow-hidden" data-testid="trace-file-card">
      <CardHeader className="header-band border-b">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="h-5 w-5 text-[hsl(var(--brand))]" />
            <span className="font-mono" data-testid="trace-prod-code">{p.production_code || "—"}</span>
            <span className="text-muted-foreground">·</span>
            <span>{p.product_type}</span>
          </CardTitle>
          <Badge variant="outline" className="capitalize">{p.status || "—"}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-5">
        {/* Subproduct (by-product) trace: lot origin + where it was consumed */}
        {file.subproduct && (
          <div className="space-y-3 rounded-lg border border-[hsl(var(--brand)/0.35)] bg-[hsl(var(--brand)/0.05)] p-3" data-testid="trace-subproduct-section">
            <SectionTitle icon={Recycle}>{t("trace.subproductSection")}</SectionTitle>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field label={t("trace.subLotCode")} value={file.subproduct.lot_code || "—"} mono />
              <Field label={t("trace.byproduct")} value={file.subproduct.name || "—"} />
              <Field label={t("trace.producedQty")} value={num(file.subproduct.quantity, ` ${file.subproduct.unit || ""}`)} />
              <Field label={t("trace.remainingQty")} value={num(file.subproduct.remaining, ` ${file.subproduct.unit || ""}`)} />
              <Field label={t("trace.date")} value={fmtDate(file.subproduct.date)} />
              {file.subproduct.source_production_code && (
                <Field label={t("trace.origin")} value={file.subproduct.source_production_code} mono />
              )}
            </div>
            <div className="space-y-2">
              <SectionTitle icon={FlaskConical}>{t("trace.consumedIn")}</SectionTitle>
              {file.subproduct.consumers?.length ? (
                <div className="overflow-x-auto rounded-md border border-border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("trace.code")}</TableHead>
                        <TableHead>{t("trace.product")}</TableHead>
                        <TableHead>{t("trace.date")}</TableHead>
                        <TableHead>{t("trace.operator")}</TableHead>
                        <TableHead className="text-right">{t("trace.qtyUsed")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {file.subproduct.consumers.map((c, i) => (
                        <TableRow key={c.production_id || i} data-testid={`trace-consumer-row-${i}`}>
                          <TableCell>
                            <TraceLink code={c.production_code} testId={`trace-consumer-code-${i}`} />
                          </TableCell>
                          <TableCell className="font-medium">{c.product_type || "—"}</TableCell>
                          <TableCell>{fmtDate(c.date)}</TableCell>
                          <TableCell>{c.operator_name || "—"}</TableCell>
                          <TableCell className="text-right font-mono">{num(c.qty, ` ${c.unit || ""}`)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="trace-no-consumers">{t("trace.noConsumers")}</p>
              )}
            </div>
          </div>
        )}

        {/* Raw material (RM-) lot trace: origin (supplier) + where it was consumed */}
        {file.raw_material && (
          <div className="space-y-3 rounded-lg border border-[hsl(var(--brand)/0.35)] bg-[hsl(var(--brand)/0.05)] p-3" data-testid="trace-raw-material-section">
            <SectionTitle icon={FlaskConical}>{t("rawMaterials.traceLotTitle")}</SectionTitle>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field label={t("rawMaterials.lotCode")} value={file.raw_material.lot_code || "—"} mono />
              <Field label={t("rawMaterials.type")} value={file.raw_material.type_name || "—"} />
              <Field label={t("rawMaterials.supplier")} value={file.raw_material.supplier_name || "—"} />
              <Field label={t("rawMaterials.supplierLot")} value={file.raw_material.supplier_lot_no || "—"} mono />
              <Field label={t("rawMaterials.quantity")} value={num(file.raw_material.qty, ` ${file.raw_material.unit || ""}`)} />
              <Field label={t("rawMaterials.remaining")} value={num(file.raw_material.remaining, ` ${file.raw_material.unit || ""}`)} />
              <Field label={t("rawMaterials.received")} value={fmtDate(file.raw_material.received_date)} />
              {file.raw_material.expiry_date && <Field label={t("rawMaterials.expiry")} value={fmtDate(file.raw_material.expiry_date)} />}
            </div>
            <div className="space-y-2">
              <SectionTitle icon={FlaskConical}>{t("rawMaterials.consumedIn")}</SectionTitle>
              {file.raw_material.consumers?.length ? (
                <div className="overflow-x-auto rounded-md border border-border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("trace.code")}</TableHead>
                        <TableHead>{t("trace.product")}</TableHead>
                        <TableHead>{t("trace.date")}</TableHead>
                        <TableHead>{t("trace.operator")}</TableHead>
                        <TableHead className="text-right">{t("trace.qtyUsed")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {file.raw_material.consumers.map((c, i) => (
                        <TableRow key={c.production_id || i} data-testid={`trace-rm-consumer-row-${i}`}>
                          <TableCell className="font-mono text-xs">{c.production_code || "—"}</TableCell>
                          <TableCell className="font-medium">{c.product_type || "—"}</TableCell>
                          <TableCell>{fmtDate(c.date)}</TableCell>
                          <TableCell>{c.operator_name || "—"}</TableCell>
                          <TableCell className="text-right font-mono">{num(c.qty, ` ${c.unit || ""}`)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="trace-rm-no-consumers">{t("rawMaterials.noConsumers")}</p>
              )}
            </div>
          </div>
        )}

        {/* Stock lot (finished goods / rebatched) trace: lot details + what it was made from */}
        {file.stock_lot && (
          <div className="space-y-3 rounded-lg border border-[hsl(var(--brand)/0.35)] bg-[hsl(var(--brand)/0.05)] p-3" data-testid="trace-stock-lot-section">
            <SectionTitle icon={Package}>{t("trace.stockLotSection")}</SectionTitle>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field label={t("trace.lotCodeLabel")} value={file.stock_lot.lot_code || "—"} mono />
              <Field label={t("trace.product")} value={file.stock_lot.product_type || "—"} />
              <Field label={t("trace.quantity")} value={num(file.stock_lot.quantity, ` ${file.stock_lot.unit || ""}`)} />
              <Field label={t("trace.remaining")} value={num(file.stock_lot.remaining, ` ${file.stock_lot.unit || ""}`)} />
              {file.stock_lot.production_date && <Field label={t("trace.madeOn")} value={fmtDate(file.stock_lot.production_date)} />}
              {file.stock_lot.packaged_date && <Field label={t("trace.packagedOn")} value={fmtDate(file.stock_lot.packaged_date)} />}
              {file.stock_lot.expiry_date && <Field label={t("trace.expiration")} value={fmtDate(file.stock_lot.expiry_date)} />}
              {file.stock_lot.production_code && <Field label={t("trace.matchProduction")} value={file.stock_lot.production_code} mono />}
            </div>
            {file.stock_lot.ancestry?.length > 0 && (
              <div className="space-y-2">
                <SectionTitle icon={Recycle}>{t("trace.madeFrom")}</SectionTitle>
                <div className="overflow-x-auto rounded-md border border-border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("trace.lotCodeLabel")}</TableHead>
                        <TableHead>{t("trace.product")}</TableHead>
                        <TableHead className="text-right">{t("trace.quantity")}</TableHead>
                        <TableHead>{t("trace.madeOn")}</TableHead>
                        <TableHead>{t("trace.expiration")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {file.stock_lot.ancestry.map((a, i) => (
                        <TableRow key={a.lot_code || i} data-testid={`trace-ancestry-row-${i}`}>
                          <TableCell className="font-mono text-xs">{a.lot_code || "—"}</TableCell>
                          <TableCell className="font-medium">{a.product_type || "—"}</TableCell>
                          <TableCell className="text-right font-mono">{num(a.quantity, ` ${a.unit || ""}`)}</TableCell>
                          <TableCell>{fmtDate(a.production_date)}</TableCell>
                          <TableCell>{fmtDate(a.expiry_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {file.stock_lot.rebatched_into?.length > 0 && (
              <div className="space-y-2">
                <SectionTitle icon={Recycle}>{t("trace.rebatchedInto")}</SectionTitle>
                <div className="overflow-x-auto rounded-md border border-border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("trace.lotCodeLabel")}</TableHead>
                        <TableHead>{t("trace.product")}</TableHead>
                        <TableHead className="text-right">{t("trace.quantity")}</TableHead>
                        <TableHead className="text-right">{t("trace.remaining")}</TableHead>
                        <TableHead>{t("trace.madeOn")}</TableHead>
                        <TableHead>{t("trace.wentTo")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {file.stock_lot.rebatched_into.map((c, i) => (
                        <TableRow key={c.lot_code || i} data-testid={`trace-descendant-row-${i}`}>
                          <TableCell className="font-mono text-xs">{c.lot_code || "—"}</TableCell>
                          <TableCell className="font-medium">{c.product_type || "—"}</TableCell>
                          <TableCell className="text-right font-mono">{num(c.quantity, ` ${c.unit || ""}`)}</TableCell>
                          <TableCell className="text-right font-mono">{num(c.remaining, ` ${c.unit || ""}`)}</TableCell>
                          <TableCell>{fmtDate(c.production_date)}</TableCell>
                          <TableCell className="text-xs">
                            {c.destinations?.length ? (
                              <span>
                                {c.destinations.map((d, di) => (
                                  <span key={di} className="block">
                                    <span className="font-medium text-foreground">{d.client_name || "—"}</span>
                                    {" · "}{num(d.quantity, ` ${d.unit || ""}`)}
                                    {d.dn_code ? <span className="font-mono"> · {d.dn_code}</span> : null}
                                    {d.date ? <span className="text-muted-foreground"> · {fmtDate(d.date)}</span> : null}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">{t("trace.stillInStock")}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {file.stock_lot.destinations?.length > 0 && (
              <div className="space-y-2">
                <SectionTitle icon={Truck}>{t("trace.thisLotWentTo")}</SectionTitle>
                <div className="flex flex-wrap gap-2">
                  {file.stock_lot.destinations.map((d, i) => (
                    <div key={i} className="rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-xs" data-testid={`trace-lot-dest-${i}`}>
                      <span className="font-medium text-foreground">{d.client_name || "—"}</span>
                      <span className="text-muted-foreground"> · {num(d.quantity, ` ${d.unit || ""}`)}</span>
                      {d.dn_code && <span className="font-mono text-muted-foreground"> · {d.dn_code}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground" data-testid="trace-stock-lot-note">
              {file.stock_lot.has_production ? t("trace.hasProductionFile") : t("trace.noProductionFile")}
            </p>
          </div>
        )}

        {/* Production summary */}
        <div className="space-y-3">
          <SectionTitle icon={FileText}>{t("trace.prodSummary")}</SectionTitle>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <Field label={t("trace.date")} value={fmtDate(p.date)} />
            <Field label={t("trace.operator")} value={p.operator_name || "—"} />
            <Field label={t("trace.totalMilk")} value={num(p.total_milk_l, " L")} />
            <Field label={t("trace.yield")} value={num(p.yield_kg, " kg")} />
            <Field label={t("trace.sold")} value={num(p.sold_kg, " kg")} />
            <Field label={t("trace.remaining")} value={num(p.remaining_kg, " kg")} />
            <Field label={t("trace.expiration")} value={fmtDate(p.expiration_date)} />
            {p.maturation_until && <Field label={t("trace.maturationUntil")} value={fmtDate(p.maturation_until)} />}
            {p.template_name && <Field label={t("trace.recipe")} value={p.template_name} />}
          </div>
          {Array.isArray(p.qc_warnings) && p.qc_warnings.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)] p-2.5" data-testid="trace-qc-warnings">
              <span className="flex items-center gap-1 text-xs font-semibold text-[hsl(var(--warning))]">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("trace.qcWarnings")}
              </span>
              {p.qc_warnings.map((w, i) => {
                if (typeof w === "string") {
                  return <p key={`${i}-s`} className="text-xs text-foreground" data-testid={`trace-qc-warning-${i}`}>{w}</p>;
                }
                return (
                  <div key={`${i}-${w?.field || ""}`} className="text-xs text-foreground" data-testid={`trace-qc-warning-${i}`}>
                    <span className="font-medium">{w?.field || "—"}:</span>{" "}
                    <span className="font-mono">{w?.value ?? "—"}</span>
                    {w?.issue && <span className="text-[hsl(var(--warning))]"> ({issueText(w.issue)})</span>}
                    {w?.corrective && (
                      <span className="block pl-3 text-muted-foreground">
                        {t("trace.corrective")}: <span className="text-foreground">{w.corrective}</span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {Array.isArray(p.subproduct_warnings) && p.subproduct_warnings.length > 0 && (
            <div className="space-y-1 rounded-md border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)] p-2.5" data-testid="trace-subproduct-warnings">
              <span className="flex items-center gap-1 text-xs font-semibold text-[hsl(var(--warning))]">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("trace.subWarnings")}
              </span>
              {p.subproduct_warnings.map((w, i) => (
                <p key={i} className="text-xs text-foreground">{String(w)}</p>
              ))}
            </div>
          )}
          {p.notes && <p className="text-sm text-muted-foreground">{t("trace.notes")}: {p.notes}</p>}
        </div>

        {/* Milk used */}
        <div className="space-y-2">
          <SectionTitle icon={Milk}>{t("trace.milkUsed")}</SectionTitle>
          {file.milk?.length ? (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("trace.batch")}</TableHead>
                    <TableHead>{t("trace.supplier")}</TableHead>
                    <TableHead className="text-right">{t("trace.litres")}</TableHead>
                    <TableHead>{t("trace.reception")}</TableHead>
                    <TableHead>{t("trace.analysis")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {file.milk.map((m, i) => {
                    const a = m.analysis || {};
                    const parts = [];
                    if (a.fat != null) parts.push(`${t("trace.fat")} ${a.fat}%`);
                    if (a.protein != null) parts.push(`${t("trace.protein")} ${a.protein}%`);
                    if (a.ph != null) parts.push(`${t("trace.ph")} ${a.ph}`);
                    if (a.temperature != null) parts.push(`${t("trace.temperature")} ${a.temperature}°`);
                    if (a.density != null) parts.push(`${t("trace.density")} ${a.density}`);
                    return (
                      <TableRow key={m.milk_batch_id || m.batch_code || i}>
                        <TableCell className="font-mono text-xs">{m.batch_code || "—"}</TableCell>
                        <TableCell>{m.supplier_name || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{num(m.qty_l, " L")}</TableCell>
                        <TableCell>{fmtDate(m.reception_date)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{parts.join(" · ") || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("trace.noMilk")}</p>
          )}
        </div>

        {/* Raw materials consumed (rennet, cultures, ...) */}
        {file.raw_materials?.length > 0 && (
          <div className="space-y-2">
            <SectionTitle icon={FlaskConical}>{t("rawMaterials.traceTitle")}</SectionTitle>
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("rawMaterials.lotCode")}</TableHead>
                    <TableHead>{t("rawMaterials.type")}</TableHead>
                    <TableHead>{t("rawMaterials.supplier")}</TableHead>
                    <TableHead>{t("rawMaterials.expiry")}</TableHead>
                    <TableHead className="text-right">{t("trace.qtyUsed")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {file.raw_materials.map((r, i) => (
                    <TableRow key={r.lot_id || i} data-testid={`trace-rm-row-${i}`}>
                      <TableCell className="font-mono text-xs">{r.lot_code || "—"}</TableCell>
                      <TableCell className="font-medium">{r.type_name || "—"}</TableCell>
                      <TableCell>{r.supplier_name || "—"}</TableCell>
                      <TableCell>{fmtDate(r.expiry_date)}</TableCell>
                      <TableCell className="text-right font-mono">{num(r.qty, ` ${r.unit || ""}`)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Subproducts (by-products) consumed as inputs — e.g. whey used to make Ricotta */}
        {file.subproducts_used?.length > 0 && (
          <div className="space-y-2">
            <SectionTitle icon={Recycle}>{t("trace.subUsedSection")}</SectionTitle>
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("trace.subLotCode")}</TableHead>
                    <TableHead>{t("trace.byproduct")}</TableHead>
                    <TableHead>{t("trace.sourceProduction")}</TableHead>
                    <TableHead className="text-right">{t("trace.qtyUsed")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {file.subproducts_used.map((s, i) => (
                    <TableRow key={s.lot_id || i} data-testid={`trace-sub-used-row-${i}`}>
                      <TableCell>
                        {s.lot_code ? (
                          <TraceLink code={s.lot_code} testId={`trace-sub-used-lot-${i}`} />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{s.name || "—"}</TableCell>
                      <TableCell>
                        <TraceLink code={s.source_production_code} testId={`trace-sub-used-src-${i}`} />
                      </TableCell>
                      <TableCell className="text-right font-mono">{num(s.qty, ` ${s.unit || ""}`)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Stock lots */}
        {file.stock_lots?.length > 0 && (
          <div className="space-y-2">
            <SectionTitle icon={Package}>{t("trace.stockLots")}</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {file.stock_lots.map((l, i) => (
                <div key={l.lot_code || i} className="rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-xs">
                  <span className="font-mono font-medium">{l.lot_code}</span>
                  <span className="text-muted-foreground"> · {num(l.remaining)}/{num(l.quantity)} {l.unit}</span>
                  {l.expiry_date && <span className="text-muted-foreground"> · {fmtDate(l.expiry_date)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sales */}
        <div className="space-y-2">
          <SectionTitle icon={Truck}>{t("trace.sales")}</SectionTitle>
          {file.sales?.length ? (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("trace.deliveryNote")}</TableHead>
                    <TableHead>{t("trace.date")}</TableHead>
                    <TableHead>{t("trace.client")}</TableHead>
                    <TableHead>{t("trace.order")}</TableHead>
                    <TableHead className="text-right">{t("trace.quantity")}</TableHead>
                    <TableHead className="text-right">{t("trace.unitPrice")}</TableHead>
                    <TableHead className="text-right">{t("trace.value")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {file.sales.map((s, i) => (
                    <TableRow key={`${s.dn_id}-${i}`}>
                      <TableCell className="font-mono text-xs">{s.dn_code || "—"}</TableCell>
                      <TableCell>{fmtDate(s.date)}</TableCell>
                      <TableCell className="font-medium">{s.client_name || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(s.order_codes || []).join(", ") || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{num(s.quantity, ` ${s.unit || ""}`)}</TableCell>
                      <TableCell className="text-right font-mono">{s.unit_price == null ? "—" : money(s.unit_price, cur)}</TableCell>
                      <TableCell className="text-right font-mono">{money(s.line_total, cur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("trace.noSales")}</p>
          )}
          {file.sales?.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-4 pt-1 text-sm">
              <span className="text-muted-foreground">
                {t("trace.totalSold")}:{" "}
                <span className="font-mono font-semibold text-foreground">
                  {totalsByUnit.map(([u, q]) => `${Number(q).toLocaleString()} ${u}`).join(" · ") || "—"}
                </span>
              </span>
              <span className="text-muted-foreground">
                {t("trace.totalValue")}:{" "}
                <span className="font-mono font-semibold text-foreground">{money(file.sales_totals?.total_value, cur)}</span>
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function Traceability() {
  const { t } = useT();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  const search = async (q) => {
    q = (q || "").trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const data = await getTraceability(q);
      setResult(data);
    } catch (err) {
      setResult(null);
      if (err?.response?.status === 404) setError(t("trace.notFound"));
      else {
        const msg = err?.response?.data?.detail || "Error";
        setError(typeof msg === "string" ? msg : "Error");
        toast.error(typeof msg === "string" ? msg : "Error");
      }
    } finally {
      setLoading(false);
    }
  };

  const runSearch = (e) => {
    e?.preventDefault?.();
    search(code);
  };

  // Cross-trace: click a lot / production code inside a result to trace it directly.
  const traceCode = (c) => {
    setCode(c);
    search(c);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="traceability-page">
        {/* Header + search */}
        <div className="no-print space-y-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <ScanSearch className="h-6 w-6 text-[hsl(var(--brand))]" />
              {t("trace.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("trace.subtitle")}</p>
          </div>
          <form onSubmit={runSearch} className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t("trace.searchPlaceholder")}
                className="pl-9 font-mono"
                data-testid="trace-search-input"
                autoFocus
              />
            </div>
            <Button type="submit" disabled={loading || !code.trim()} data-testid="trace-search-button">
              {loading ? t("trace.searching") : t("trace.searchBtn")}
            </Button>
            {result?.count > 0 && (
              <Button type="button" variant="outline" onClick={() => window.print()} data-testid="trace-print-button">
                <Printer className="mr-1.5 h-4 w-4" /> {t("trace.print")}
              </Button>
            )}
          </form>
        </div>

        {/* States */}
        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {!loading && error && (
          <Card className="no-print">
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground" data-testid="trace-not-found">{error}</p>
            </CardContent>
          </Card>
        )}

        {!loading && !error && !searched && (
          <Card className="no-print">
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <ScanSearch className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-base font-semibold text-foreground">{t("trace.emptyTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("trace.emptyHint")}</p>
            </CardContent>
          </Card>
        )}

        {/* Results (printable) */}
        {!loading && !error && result?.count > 0 && (
          <div id="trace-report" className="space-y-5">
            {/* Print-only header */}
            <div className="hidden print:block">
              <h2 className="text-lg font-bold">{t("trace.reportTitle")} — LactoPilot</h2>
              <p className="text-xs">{t("trace.auditNote")} · {t("trace.generatedAt")}: {formatDateTime(new Date())}</p>
              <hr className="my-2" />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t("trace.resultsFor")} <span className="font-mono font-semibold text-foreground">{result.query}</span>
              </span>
              {result.matched_by && (
                <Badge variant="secondary" data-testid="trace-matched-by">
                  {t("trace.matchedBy")}: {t(matchKey[result.matched_by] || "trace.matchProduction")}
                </Badge>
              )}
              <span className="text-sm text-muted-foreground">· {result.count} {t("trace.filesFound")}</span>
            </div>

            {result.productions.map((file, i) => (
              <ProductionFile key={`${file.production.id}-${file.subproduct?.lot_code || i}`} file={file} t={t} onTrace={traceCode} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
